<script lang="ts">
  import { tick } from "svelte";
  import type { BridgeId, PublicBridge, PublicSnapshot, PublicVesselGroup, PublicVesselSummary } from "../../shared/contracts";
  import { BRIDGE_STATIONS } from "../../shared/corridor";
  import { bridgeStation, routeDiagramPoint, svgRoutePath } from "../../shared/river-model";
  import { bridgePositionCopy, formatEtTime, freshnessLabel, vesselDisplayName } from "../format";
  import { layoutVesselDiagram, diagramEstimatePath } from "../vessel-diagram-layout";
  import { trafficMovements, movementOrdinals } from "../tow-groups";
  import BasculeGlyph from "./BasculeGlyph.svelte";

  export let active = true;
  export let bridges: PublicBridge[];
  export let vessels: PublicVesselSummary[];
  export let vesselGroups: PublicVesselGroup[] = [];
  export let selectedBridgeId: BridgeId = "brickell";
  export let selectedVesselId: string | null = null;
  export let viewRevision = 0;
  export let dataMode: PublicSnapshot["dataMode"] = "live";
  export let onvesselselect: (vessel: PublicVesselSummary, trigger: HTMLElement) => void;

  let viewport: HTMLDivElement;
  let viewportWidth = 360;
  // A portrait transform of the existing atlas, not another route or milepost
  // model. All bridge anchors, fixes, estimates and tow links share this matrix.
  const projection = "matrix(0 0.9 0.26 0 16 44)";
  const point = (p: { x: number; y: number }): { x: number; y: number } => ({ x: 16 + p.y * 0.26, y: 44 + p.x * 0.9 });
  $: touchSize = 44 * Math.max(1, 360 / (viewportWidth || 360));
  $: bridgeById = new Map(bridges.map(bridge => [bridge.id, bridge]));
  $: displayed = layoutVesselDiagram(vessels, vesselGroups);
  $: movements = trafficMovements(vessels, vesselGroups);
  $: ordinals = movementOrdinals(movements);
  $: if (viewport && active) void revealBridge(selectedBridgeId, viewRevision);

  async function revealBridge(id: BridgeId, _revision: number): Promise<void> {
    await tick();
    const target = viewport?.querySelector<SVGGElement>(`[data-bridge-id="${id}"]`);
    if (!target) return;
    const bounds = target.getBoundingClientRect(), frame = viewport.getBoundingClientRect();
    // Mutating the bound viewport variable here invalidates its reactive
    // statement in Svelte, repeatedly scheduling another tick. Use a local DOM
    // reference so revealing a station runs once per selection.
    const element = viewport;
    element.scrollTop += bounds.top + bounds.height / 2 - frame.top - frame.height / 2;
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_tabindex (The scrollable region needs keyboard focus for native arrow-key scrolling.) -->
<div class="mobile-corridor" bind:this={viewport} bind:clientWidth={viewportWidth} style={`--map-text-scale: ${Math.max(1, 360 / (viewportWidth || 360))}`} role="region" aria-label="Miami River corridor, scroll from upriver to Biscayne Bay" tabindex="0">
  <svg viewBox="0 0 360 1250" class="mobile-corridor__map" role="group" aria-labelledby="mobile-corridor-title">
    <title id="mobile-corridor-title">All nine Miami River bridges and vessel positions</title>
    <text class="mobile-corridor__direction" x="18" y="26">UPRIVER</text>
    <text class="mobile-corridor__hint" x="342" y="26" text-anchor="end">Scroll along the river</text>
    <g transform={projection} aria-hidden="true" class="mobile-corridor__routes">
      {#each ["miami_river", "north_approach", "government_cut", "south_approach"] as route}
        <path class="mobile-corridor__water" d={svgRoutePath(route as PublicVesselSummary["corridor"])} />
        <path class="mobile-corridor__route" d={svgRoutePath(route as PublicVesselSummary["corridor"])} />
      {/each}
      {#each movements.filter(movement => movement.kind === "likely_tow") as movement (movement.id)}
        <polyline class="mobile-corridor__tow" points={movement.vessels.map(vessel => {
          const v = displayed.get(vessel.id) ?? vessel;
          const p = routeDiagramPoint(v.corridor, v.sMeters, v.corridorProgress);
          return `${p.x},${p.y}`;
        }).join(" ")} />
      {/each}
    </g>
    {#each BRIDGE_STATIONS.slice().reverse() as station, index (station.id)}
      {@const bridge = bridgeById.get(station.id)}
      {@const p = point(bridgeStation(station.id).diagram)}
      {#if bridge}
        <g transform={`translate(0 ${p.y})`} data-bridge-id={station.id} class:station--up={bridge.state === "up"} class:station--unknown={bridge.state === "unknown"} class:mobile-corridor__station--selected={selectedBridgeId === station.id} class="mobile-corridor__station">
          <title>{station.name}: {bridgePositionCopy[bridge.state]}. {freshnessLabel(bridge.freshness, dataMode)}. Last confirmed {(bridge.lastConfirmedAt ? formatEtTime(bridge.lastConfirmedAt) : "Not confirmed")}</title>
          <path class="mobile-corridor__leader" d={`M${p.x + 24} 0H164`} aria-hidden="true" />
          <g transform={`translate(${p.x} 0)`} aria-hidden="true"><BasculeGlyph angle={bridge.state === "up" ? 54 : 0} scale={0.62} variant={index % 3} /></g>
          <text class="mobile-corridor__name" x="172" y="-8">{station.id === "brickell" ? "Brickell Avenue" : station.shortName}</text>
          <text class="mobile-corridor__state" x="172" y="9">{bridgePositionCopy[bridge.state]}</text>
          <text class="mobile-corridor__freshness" x="172" y="24">{freshnessLabel(bridge.freshness, dataMode)} · {(bridge.lastConfirmedAt ? formatEtTime(bridge.lastConfirmedAt) : "Not confirmed")}</text>
        </g>
      {/if}
    {/each}
    {#each vessels as vessel, index (vessel.id)}
      {@const display = displayed.get(vessel.id) ?? vessel}
      {@const reported = point(routeDiagramPoint(vessel.corridor, vessel.sMeters, vessel.corridorProgress))}
      {@const p = point(routeDiagramPoint(display.corridor, display.sMeters, display.corridorProgress))}
      {#if Math.hypot(p.x - reported.x, p.y - reported.y) > 0.5}
        <path d={diagramEstimatePath(vessel, display)} transform={projection} class="mobile-corridor__estimate" aria-hidden="true" />
        <circle cx={reported.x} cy={reported.y} r="3" class="mobile-corridor__reported"><title>Reported position</title></circle>
      {/if}
      <foreignObject x={p.x - touchSize / 2} y={p.y - touchSize / 2} width={touchSize} height={touchSize}>
        <button class:mobile-corridor__vessel--selected={selectedVesselId === vessel.id} class="mobile-corridor__vessel" type="button" aria-label={`View ${vesselDisplayName(vessel)}, ${vessel.bridgePhase} Brickell`} aria-pressed={selectedVesselId === vessel.id} onclick={(event) => onvesselselect(vessel, event.currentTarget)}>
          <span>{ordinals.get(vessel.id) ?? index + 1}</span>
        </button>
      </foreignObject>
    {/each}
    <text class="mobile-corridor__name" x="190" y="1156">Biscayne Bay</text>
    <text class="mobile-corridor__freshness" x="190" y="1176">Three inbound approaches</text>
    <text class="mobile-corridor__hint" x="18" y="1233">Vessel numbers match the traffic list.</text>
  </svg>
</div>

<style>
  .mobile-corridor { height: 100%; overflow: auto; overscroll-behavior-y: contain; background: var(--instrument); scrollbar-width: thin; scrollbar-color: var(--harbor-water) var(--instrument); }
  .mobile-corridor:focus-visible { outline: 2px solid var(--harbor-water); outline-offset: -3px; }
  .mobile-corridor__map { display: block; width: 100%; min-height: 100%; }
  .mobile-corridor__water { fill: none; stroke: var(--live-cyan); stroke-width: 30; stroke-linecap: round; }
  .mobile-corridor__route { fill: none; stroke: var(--harbor-water); stroke-width: 3; }
  .mobile-corridor__leader { fill: none; stroke: var(--instrument-rule); stroke-width: 1; }
  .mobile-corridor__name { fill: var(--instrument-ink); font-family: "Barlow Condensed", sans-serif; font-size: calc(16px * var(--map-text-scale)); font-weight: 800; }
  .mobile-corridor__state { fill: var(--instrument-ink); font-size: calc(11px * var(--map-text-scale)); font-weight: 800; }
  .mobile-corridor__freshness, .mobile-corridor__hint { fill: var(--instrument-muted); font-size: calc(11px * var(--map-text-scale)); }
  .mobile-corridor__direction { fill: var(--instrument-ink); font-size: calc(11px * var(--map-text-scale)); font-weight: 800; }
  .mobile-corridor__station--selected .mobile-corridor__name { fill: var(--harbor-water); }
  .mobile-corridor__vessel { display: grid; place-items: center; width: 100%; height: 100%; padding: 0; border: 0; color: var(--instrument-strong); background: transparent; cursor: pointer; }
  .mobile-corridor__vessel span { display: grid; width: 24px; height: 24px; place-items: center; border: 2px solid var(--instrument-strong); border-radius: 50%; background: var(--harbor-deep); font-size: calc(11px * var(--map-text-scale)); font-weight: 800; }
  .mobile-corridor__vessel--selected span { background: var(--harbor-water); }
  .mobile-corridor__vessel:hover span { background: var(--harbor-water); }
  .mobile-corridor__vessel:focus-visible { outline: 2px solid var(--instrument-ink); outline-offset: -3px; }
  .mobile-corridor__tow { fill: none; stroke: var(--harbor-deep); stroke-width: 5; stroke-dasharray: 5 4; }
  .mobile-corridor__estimate { fill: none; stroke: var(--road-amber-ink); stroke-width: 3; stroke-dasharray: 5 4; }
  .mobile-corridor__reported { fill: var(--road-amber); }
</style>
